<template>
  <VToolbar
    v-if="isAgent || showImpersonationHeader"
    :color="color"
    data-testid="agent-header"
    :class="{'header--impersonating': isImpersonating}"
    class="header--agent hidden-md-and-down "
    text
  >
    <VToolbarItems
      v-if="isImpersonating"
      class="align-center"
    >
      <VRow no-gutters class="align-center">
        <SBtn
          :loading="loading"
          color="primary"
          class="pa-6 font16"
          @click="clearImpersonation"
        >
          <span>{{ $t('agent.btn.clear') }}</span>
        </SBtn>
        <span class="ml-2 text-white text-uppercase font16 font-weight-medium">
          {{ `${$t('agent.label.impersonating')}: ${userFullName}` }}
          <span v-if="isGuest">&nbsp;({{ guestAccount }})</span>
        </span>
        <!-- Without this an agent has no on-screen signal that the session they are
             impersonating in is date-shifted, and every price/promotion they read to
             the customer is wrong. nuxt2 renders it here; the port dropped it.
             White pill rather than nuxt2's bare `error--text`: nuxt2 sat this on a
             white VCol, whereas this banner is `secondary`/`greyedOut`, and red on
             either of those is below 3:1. `errorText` rather than `error` because
             #ff0000 on the white pill is only 3.998:1 at this size (WCAG 1.4.3). -->
        <span
          v-if="viewAsDate"
          class="ml-2 px-2 py-1 rounded bg-white text-errorText font16 font-weight-bold"
          data-testid="date-shifted-indicator"
        >
          {{ $t('agent.label.dateShiftedTest') }}
        </span>
      </VRow>
    </VToolbarItems>
    <VToolbarItems
      v-else
      class="align-center"
    >
      <ObservedForm
        :loading="loading"
        noerror
        @submit="submit"
      >
        <VRow align="center" no-gutters class="mt-0 ">

          <VCol >
            <TextFieldWithValidation
              v-model="username"
              name="username"
              :label="$t('label.userID')"
              rules="required|max:75"
              width="200px"
              class="mr-2"
              hide-details-without-error
              data-testid="impersonate-username"
            />
            <ErrorContainer
              v-if="authError"
              :messages="[errorMessage]"
              class="error-container"
              no-border
              xs
            />
          </VCol>
          <VCol>
            <SBtn
              type="submit"
              color="secondary font16"
              link-medium
              class="px-6 mr-2"
              style="text-overflow:clip"
              data-testid="impersonate-button"
            >
              <span>{{ $t('agent.btn.impersonate') }}</span>
            </SBtn>
          </VCol>
        </VRow>
      </ObservedForm>
    </VToolbarItems>
    <template v-if="!isImpersonating">
    <VToolbarItems
      class="align-center"
    >
      <VRow no-gutters class="mr-8 justify-right">
        <VCol cols="12">
          <VSwitch
            :model-value="!translated"
            inset
            class="font16 pa-1 pt-0"
            hide-details
            :label="$t('agent.btn.untranslate')"
            @click="untranslate"
          />
        </VCol>
      </VRow>
    </VToolbarItems>
    <VToolbarItems
      class="align-center"
    >
      <VRow no-gutters class="mr-8 justify-right">
        <VCol cols="12">
          <VSwitch
            :model-value="isDemo"
            inset
            class="font16 pa-1"
            hide-details
            :label="view"
            @click="changeView"
          />
        </VCol>
      </VRow>
    </VToolbarItems>
    </template>
    <VSpacer />

    <!-- Available while impersonating too: shifting the date IS the impersonated
         test. Hiding the picker (and disabling the menu/editor) when impersonating
         made the date-shifted state unreachable through the UI, which is why the
         restored guards below could never fire. Ports nuxt2 PR 22432 (AB#60489). -->
    <VToolbarItems class="align-center pr-6">
      <VRow
        class="align-center"
        :class="{ 'date-field--boxed': isImpersonating }"
        no-gutters
      >
        <VMenu
          v-model="showMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          z-index="2147483641"
        >
          <template #activator="{props: menuProps}">
            <VTextField
              :model-value="viewAsDateHuman"
              :label="$t('agent.label.date')"
              :prepend-icon="icons.mdiCalendar"
              :append-icon="icons.mdiClose"
              readonly
              hide-details
              class="mr-6 v-text-date"
              width="250px"
              v-bind="menuProps"
              @click:append="setDate(null)"
            />
          </template>
          <!-- Lazy: the date/time pickers are ~40KB that only an agent opening this
               menu needs; imported here they sat in every visitor's entry chunk. -->
          <LazyBaseFieldsDateTimePicker
            :min="minDate"
            color="secondary"
            no-title
            @input="setDateAndClose"
          />
        </VMenu>
      </VRow>
    </VToolbarItems>
    <VToolbarItems>
      <VRow no-gutters class="align-center">
        <SBtn
          :loading="loading"
          color="primary"
          class="pa-4 font16"
          @click="agentLogout"
        >
          <span>{{ $t('agent.btn.logout') }}</span>
        </SBtn>
      </VRow>
    </VToolbarItems>
  </VToolbar>
</template>

<script lang="ts" setup>
import { mdiCalendar, mdiClose } from '@mdi/js'
import ErrorContainer from '~/components/base/ErrorContainer.vue'
import TextFieldWithValidation from '~/components/base/fields/TextFieldWithValidation.vue'
import ObservedForm from '~/components/base/ObservedForm.vue'
import { UserRole } from '~/types/account'
import { useAgentStore } from '~/stores/agent'
import { useRootStore } from '~/stores/root'
import { usePersistentStore } from '~/stores/persistent'

const icons = { mdiCalendar, mdiClose }
const username = ref('')
const authError = ref(false)
const showMenu = ref(false)
const isSameDomain = ref(true)

const { $appInsights } = useNuxtApp()
const $i18n = useI18n()
const agentStore = useAgentStore()
const persistentStore = usePersistentStore()
const { loading, processAction } = useAction()
// TOdO: Add translations
const guestAccount = 'Guest account'

const translated = computed(() => !agentStore.noTranslate)

const rootStore = useRootStore()

const { isDemo, isAgent, isGuest, user } = useSAuthState()
const isImpersonating = computed(() => persistentStore.impersonationContext.isImpersonating && isSameDomain.value)
const viewAsDate = computed(() => agentStore.viewAsDate)

watch(isImpersonating, async () => {
  if (isImpersonating.value) {
    $appInsights.trackTrace({
      message: 'Agent impersonation started',
      properties: {
        agentEmail: persistentStore.impersonationContext.agentEmail || 'unknown',
        impersonatedEmail: persistentStore.impersonationContext.impersonatedEmail,
        impersonatedRole: persistentStore.impersonationContext.impersonatedRole
      }
    })

    try {
      await agentStore.checkImpersonationAuthorization()
    } catch {
      // do nothing
    }
  }
}, { immediate: true })

const showImpersonationHeader = computed(() => agentStore.showImpersonationHeader)
const demoViewAsDate = computed(() => persistentStore.getPersistentValue('demoViewAsDate'))

const userFullName = computed(() => user.value ? `${user.value.firstName} ${user.value.lastName}` : '')
// Banner colour follows nuxt2 (`role !== 'Guest' ? 'secondary' : 'greyedOut'`): a real
// signed-in account gets the brand `secondary`, a guest account the muted `greyedOut`.
// The nuxt4 port inverted the ternary, so the agent's visual cue for "this is a guest
// account" meant the opposite of production. AB#63280
const color = computed(() => isImpersonating.value ? isGuest.value ? 'greyedOut' : 'secondary' : '')
const errorMessage = computed(() => $i18n.t('agent.error.login'))
const minDate = computed(() => {
  const now = new Date()
  now.setDate(now.getDate() - 2)
  return now.toISOString()
})
const view = computed(() => isDemo.value ? 'Demonstrator Site' : 'Customer Site')
const viewAsDateHuman = computed(() => {
  return viewAsDate.value?.replace('T', ' ').replace('Z', '') ??
    demoViewAsDate.value?.replace('T', ' ').replace('Z', '')
})

const { redirectHome } = useRedirect()
watch(username, () => { authError.value = false })

const changeView = () => {
  processAction(async () => {
    await agentStore.changeWebsiteRole(isDemo.value ? UserRole.Customer : UserRole.Demonstrator)
    redirectHome()
  })
}
const setDateAndClose = (viewAsDate: string | null | undefined) => {
  processAction(async () => {
    await setDate(viewAsDate)
    showMenu.value = false
    await refreshNuxtData()
  })
}
const setDate = async (viewAsDate: string | null | undefined) => {
  agentStore.updateViewAsDate(viewAsDate ?? null)
  await rootStore.fetchCategories()

  // should only be used for testing different commissions periods on portal dashboards
  // we don't want agents to use this in prod because they could add a product with a bad date/ priceDate to a real demo account
  await persistentStore.updateValue('demoViewAsDate', viewAsDate ?? null)
}
const clearImpersonation = () => {
  processAction(async () => {
    // redirect to home page to avoid unauthorized request
    redirectHome()
    await agentStore.clearImpersonation()
    username.value = ''
    // redirect again to avoid demo home page
    redirectHome()
  })
}
const agentLogout = () => {
  processAction(async () => {
    agentStore.updateViewAsDate(null)
    await persistentStore.updateValue('demoViewAsDate', null)
    if (!translated.value) {
      agentStore.untranslate()
    }
    // Agent sessions live in backend-set cookies (SameSite=None), so the backend
    // must clear them and hand back the storefront URL to navigate to. A
    // client-only logout would leave the backend OIDC session intact.
    await agentStore.signOutOidc()
    // showToast=true: if the sign-out POST throws, the backend session was NOT
    // cleared (the agent is still signed in) and signOutOidc never navigates, so
    // surface a toast to retry rather than failing silently on a dead page.
  }, undefined, true)
}
const untranslate = async () => {
  agentStore.untranslate()
  await nextTick()
}
const submit = () => {
  processAction(async () => {
    try {
      // The date deliberately survives impersonating: an agent sets the date, then
      // impersonates the customer to see that date's pricing. Clearing it here
      // reset every impersonated session to today. Ports nuxt2 PR 22432 (AB#60489).
      isSameDomain.value = await agentStore.impersonate({ email: username.value })
      if (isSameDomain.value) {
        redirectHome()
      }
    } catch {
      authError.value = true
    }
  })
}
</script>

<style lang='scss'>
  .header--agent {
    z-index: 11;
    background-color: rgb(var(--v-theme-backgroundDiv));
    max-height: 64px;

    .label--impersonating {
      color: white;
    }

    .error-container {
      height: 56px;
    }

    .v-toolbar__content {
      padding-left: 12px;
      padding-right: 12px;
    }
    // v3 renamed these: .v-text-field__details / .v-input__slot are Vuetify 2
    // selectors that match nothing here, so the reserved detail space survived
    // the port. With details hidden, v-input--indent-details still reserves
    // 10px of padding-bottom, which pushes the field off the 64px bar's centre.
    .v-input--indent-details {
      padding-bottom: 0;
    }
    .v-text-date {
      width: 196px;
    }
    // White box behind the view-as date control so it stays readable against the
    // secondary/greyedOut impersonation banner. Extra top padding contains the
    // floating "Viewing As Date" label, whose reserved space .v-text-date collapses.
    //
    // padding-bottom is LOAD-BEARING for the focus ring, not just spacing. The
    // field paints a 2px ring at outline-offset 0 (global.scss, the
    // `.v-text-field:focus-within .v-input__control .v-field` rule), so the ring
    // is drawn inside this box and stays on white at 3.39:1. Shrink this below
    // 4px and the ring reaches the banner, where focusRing (#43989A) is 1.47:1
    // on secondary and 1.34:1 on greyedOut - both under the 3:1 non-text minimum
    // (WCAG 1.4.11 / 2.4.7). 4px leaves 2px of clearance; 2px left none.
    .date-field--boxed {
      background-color: rgb(var(--v-theme-white));
      border-radius: 4px;
      padding: 18px 12px 4px;
    }
  }
</style>
